import { useCallback, useEffect, useState } from 'react';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import { api } from '../../lib/api.js';
import { staffActionLabel, staffActivityRows, staffResultLabel } from '../../lib/staff-act.js';

const when = (iso) => {
  const d = new Date(iso);
  return iso && !Number.isNaN(d.getTime()) ? d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC' : '';
};

const COLUMNS = [
  { id: 'time', header: 'When', sortValue: (r) => r.at ?? '', cell: (r) => when(r.at) },
  { id: 'who', header: 'Who', sortValue: (r) => r.actorName ?? '', cell: (r) => <span>{r.actorName || 'IPC Global'} <span className="text-ipc-muted">({r.actorOrg || 'IPC Global'})</span></span> },
  { id: 'what', header: 'What', sortValue: (r) => r.action ?? '', cell: (r) => staffActionLabel(r.action) },
  { id: 'result', header: 'Result', sortValue: (r) => r.result ?? '', cell: (r) => staffResultLabel(r.result) },
];

const list = (v) => (Array.isArray(v) ? v : []);

/**
 * What IPC Global staff did on this customer (ENTRAID-307, Decision 2): who (the staff member and "IPC Global"), what
 * and when, from the customer's own audit rows (GET .../compliance/audit?who=staff). The customer's admins read it;
 * the portal enforces that and keys every row on this customer.
 *
 * @param {{ customerId: string }} props
 */
export default function StaffActivity({ customerId }) {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const out = await api.staffActivity(customerId);
      setEvents(list(out?.events));
      setCursor(typeof out?.nextCursor === 'string' ? out.nextCursor : null);
    } catch {
      setEvents([]);
      setError('IPC Global activity could not be loaded. Try again.');
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  async function more() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const out = await api.staffActivity(customerId, { cursor });
      setEvents((prev) => [...list(prev), ...list(out?.events)]);
      setCursor(typeof out?.nextCursor === 'string' ? out.nextCursor : null);
    } catch {
      setError('More activity could not be loaded. Try again.');
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <SectionCard title="IPC Global activity" testId="staff-activity">
      <p className="mb-3 text-sm text-ipc-muted">Every action IPC Global staff took on your onGuard account, newest first.</p>
      <ConsoleTable
        label="IPC Global activity"
        columns={COLUMNS}
        rows={staffActivityRows(events ?? [])}
        getRowId={(r) => r.eventId ?? `${r.at}-${r.action}-${r.result}`}
        rowName={(r) => staffActionLabel(r.action)}
        defaultSort={{ columnId: 'time', direction: 'descending' }}
        loading={events === null && !error}
        error={error}
        onRetry={load}
        hasMore={cursor !== null}
        onLoadMore={more}
        loadingMore={loadingMore}
        emptyState={{ title: 'No IPC Global activity yet', body: 'When IPC Global staff act on your account, it appears here.' }}
      />
    </SectionCard>
  );
}
